<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { DrawerBottom } from '@ilteducation/components';

const props = defineProps<{
  modelValue: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  errorMessage?: string;
  error?: boolean;
  loading?: boolean;
  confirmColor?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'confirm'): void;
}>();

const { smAndDown } = useDisplay();

const handleClose = () => {
  if (props.loading) {
    return;
  }
  emit('update:modelValue', false);
};

const handleConfirm = () => {
  emit('confirm');
};
</script>

<template>
  <!-- Mobile: bottom drawer -->
  <DrawerBottom v-if="smAndDown" :model-value="modelValue" :title="title" @close="handleClose">
    <div class="d-flex flex-column ga-4 pt-2 pb-4">
      <p class="chalkboard-font text-body-1 ma-0">
        {{ message }}
      </p>
      <p v-if="error" class="text-error text-body-2 ma-0">
        {{ errorMessage }}
      </p>
      <div class="d-flex justify-end ga-4 align-center">
        <v-btn size="large" variant="outlined" rounded="default" :disabled="loading" @click="handleClose">
          {{ cancelLabel }}
        </v-btn>
        <v-btn
          size="large"
          :color="confirmColor ?? 'primary'"
          variant="flat"
          rounded="default"
          :loading="loading"
          @click="handleConfirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </div>
    </div>
  </DrawerBottom>

  <!-- Desktop: centered dialog with chalk theme -->
  <v-dialog
    v-else
    :model-value="modelValue"
    max-width="500"
    min-width="250"
    @update:model-value="handleClose"
    @keydown.esc="handleClose"
    @click:outside="handleClose"
  >
    <v-card class="rounded-lg w-100">
      <v-card-title class="py-6 text-center">
        <h2 class="chalkduster-font ma-0">{{ title }}</h2>
      </v-card-title>

      <v-card-text class="px-6 text-center">
        <p class="chalkboard-font text-body-1 ma-0">
          {{ message }}
        </p>
        <p v-if="error" class="text-error text-body-2 mt-4 mb-0">
          {{ errorMessage }}
        </p>
      </v-card-text>

      <v-card-actions class="pa-6 d-flex ga-4 justify-end">
        <v-btn size="large" variant="outlined" rounded="default" :disabled="loading" @click="handleClose">
          {{ cancelLabel }}
        </v-btn>
        <v-btn
          size="large"
          :color="confirmColor ?? 'primary'"
          variant="flat"
          rounded="default"
          :loading="loading"
          @click="handleConfirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
